<template>
    <div class="page-container">

        <CrmPageHeader
            title="Credenciales de Portales"
            subtitle="Matriz de sincronización por usuario y portal"
            icon="fas fa-key"
        >
            <soft-button variant="gradient" size="sm" @click="openAssignModal()">
                <i class="fas fa-share-alt me-1"></i>
                Compartir
            </soft-button>
            <soft-button variant="outline" color="secondary" size="sm"
                @click="fetchUsers()" :loading="spinner">
                <i class="fas fa-sync-alt me-1"></i>
                Actualizar
            </soft-button>
        </CrmPageHeader>

        <!-- Alert -->
        <soft-alert v-if="alertTable.state" :color="alertTable.color" class="mb-4" dismissible>
            {{ alertTable.message }}
        </soft-alert>

        <!-- Matrix Card -->
        <div class="crm-card">
            <!-- Filters -->
            <div class="crm-card__header">
                <div class="matrix-filters">
                    <div class="filter-group">
                        <label class="filter-label">Correo</label>
                        <div class="filter-input-wrap">
                            <i class="fas fa-envelope filter-input-icon"></i>
                            <input
                                type="text"
                                class="filter-input"
                                v-model="filterEmail"
                                placeholder="Buscar por email..."
                                @keyup.enter="fetchUsers()"
                            />
                        </div>
                    </div>
                    <div class="filter-group">
                        <label class="filter-label">Código</label>
                        <div class="filter-input-wrap">
                            <i class="fas fa-hashtag filter-input-icon"></i>
                            <input
                                type="text"
                                class="filter-input"
                                v-model="filterCode"
                                placeholder="Buscar por código..."
                                @keyup.enter="fetchUsers()"
                            />
                        </div>
                    </div>
                    <div class="filter-actions">
                        <button class="cred-btn cred-btn--primary" @click="fetchUsers()" :disabled="spinner">
                            <i class="fas fa-search"></i>
                            Filtrar
                        </button>
                        <button
                            v-if="filterEmail || filterCode"
                            class="cred-btn cred-btn--outline"
                            @click="filterEmail = ''; filterCode = ''; fetchUsers()"
                        >
                            <i class="fas fa-times"></i>
                            Limpiar
                        </button>
                    </div>
                </div>
            </div>

            <div class="crm-card__body p-0">
                <div class="table-responsive">
                    <soft-spinner v-if="spinner" />
                    <table v-else class="matrix-table">
                        <thead>
                            <tr>
                                <th class="matrix-th matrix-th--sticky">
                                    <span>Usuario</span>
                                </th>
                                <th
                                    v-for="portal in portales"
                                    :key="portal.id"
                                    class="matrix-th matrix-th--portal"
                                >
                                    <div class="portal-head">
                                        <img
                                            v-if="portal.image"
                                            :src="portal.image"
                                            :alt="portal.name"
                                            class="portal-logo"
                                            @error="onImgError($event)"
                                        />
                                        <span>{{ portal.name }}</span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="userItem in usersData"
                                :key="userItem.user.id"
                                :class="{ 'matrix-row--admin': userItem.is_admin }"
                            >
                                <!-- Sticky user column -->
                                <td class="matrix-td matrix-td--sticky" :class="{ 'matrix-td--admin': userItem.is_admin }">
                                    <div class="user-cell">
                                        <div class="user-avatar" :class="userItem.is_admin ? 'user-avatar--admin' : 'user-avatar--sub'">
                                            <span>{{ getInitials(userItem.user) }}</span>
                                        </div>
                                        <div class="user-info">
                                            <p class="user-name">
                                                {{ userItem.user.name }} {{ userItem.user.last_name || '' }}
                                            </p>
                                            <p class="user-email">
                                                {{ userItem.user.email }}
                                                <span v-if="userItem.is_admin" class="admin-chip">Admin</span>
                                            </p>
                                        </div>
                                    </div>
                                </td>

                                <!-- Portal cells -->
                                <td
                                    v-for="portal in portales"
                                    :key="portal.id"
                                    class="matrix-td matrix-td--cell"
                                    @click="onCellClick(userItem, portal)"
                                >
                                    <template v-if="getCredentialType(getUserPortalInfo(userItem, portal.id))">
                                        <span
                                            class="cred-chip"
                                            :class="getCredentialType(getUserPortalInfo(userItem, portal.id)) === 'propia'
                                                ? 'cred-chip--own' : 'cred-chip--shared'"
                                        >
                                            {{ getCredentialType(getUserPortalInfo(userItem, portal.id)) === 'propia'
                                                ? 'Propia' : 'Compartida' }}
                                        </span>
                                        <span
                                            v-if="getUserPortalInfo(userItem, portal.id)?.status"
                                            class="cred-status"
                                            :class="getUserPortalInfo(userItem, portal.id).status === 'active'
                                                ? 'cred-status--active' : 'cred-status--inactive'"
                                        >
                                            <i class="fas fa-circle"></i>
                                            {{ getUserPortalInfo(userItem, portal.id).status === 'active' ? 'Activo' : 'Inactivo' }}
                                        </span>
                                    </template>
                                    <template v-else>
                                        <span class="cred-empty"><i class="fas fa-minus"></i></span>
                                    </template>
                                </td>
                            </tr>

                            <tr v-if="usersData.length === 0 && !spinner">
                                <td :colspan="portales.length + 1" class="text-center py-5">
                                    <i class="fas fa-inbox d-block mb-2" style="font-size:2rem;color:#CBD5E1"></i>
                                    <span style="font-size:.82rem;color:#94A3B8">No se encontraron usuarios</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Cell Actions Modal -->
        <cell-actions
            v-if="showCellActions && selectedCell"
            :cell="selectedCell"
            :showAssignOption="!selectedCell.user.is_admin"
            @close="closeCellActions"
            @create="openCredentialForm(selectedCell.user, selectedCell.portal, false)"
            @edit="openCredentialForm(selectedCell.user, selectedCell.portal, true)"
            @delete="deleteCredential"
            @revoke="revokeShared"
            @toggle-status="toggleStatus(selectedCell.portal.id, selectedCell.user.id)"
            @assign="singleAssign"
        />

        <!-- Credential Form Modal -->
        <soft-modal
            v-if="showCredentialForm && selectedPortal && selectedUser"
            :modalActive="showCredentialForm"
            @close="closeCredentialForm"
            size="lg"
        >
            <template #header>
                <div style="display:flex;align-items:center;gap:10px">
                    <div style="width:32px;height:32px;border-radius:8px;background:linear-gradient(135deg,#1E3A5F,#2563EB);display:flex;align-items:center;justify-content:center;color:#fff;font-size:.85rem">
                        <i class="fas fa-key"></i>
                    </div>
                    <span>Credencial — {{ selectedPortal.name }}</span>
                </div>
            </template>
            <credential-form-modal
                :portal="selectedPortal"
                :user="selectedUser"
                :fields="getPortalFields(selectedPortal.id, selectedPortal.slug)"
                :existingData="existingCredentialData"
                :isLoading="isLoading"
                @save="saveCredential"
                @cancel="closeCredentialForm"
            />
        </soft-modal>

        <!-- Batch Assign Modal -->
        <soft-modal
            v-if="showAssignModal"
            :modalActive="showAssignModal"
            @close="closeAssignModal"
            size="xl"
        >
            <template #header>
                <div style="display:flex;align-items:center;gap:10px">
                    <div style="width:32px;height:32px;border-radius:8px;background:linear-gradient(135deg,#0284C7,#0EA5E9);display:flex;align-items:center;justify-content:center;color:#fff;font-size:.85rem">
                        <i class="fas fa-share-alt"></i>
                    </div>
                    <span>Compartir Credenciales</span>
                </div>
            </template>
            <assign-modal
                :preSelectedPortal="selectedPortal"
                :users="membershipUsers"
                :portales="portales"
                :portalCredentials="portalCredentialsList"
                :loadingPortalCredentials="loadingPortalCredentials"
                :pagination="membershipPagination"
                :loadingMembers="loadingMembers"
                :isLoading="isLoading"
                @assign="batchAssign"
                @unassign="batchUnassign"
                @delete-share="deleteShare"
                @search="fetchMembershipUsers"
                @portal-change="fetchPortalCredentialsList"
                @cancel="closeAssignModal"
            />
        </soft-modal>

        <!-- Loading Overlay -->
        <div v-if="isLoading" class="loading-overlay">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
            </div>
        </div>
    </div>
</template>

<script>
import SoftButton from "@/components/SoftButton.vue";
import SoftModal from "@/components/SoftModal.vue";
import SoftSpinner from "@/components/SoftSpinner.vue";
import SoftAlert from "@/components/SoftAlert.vue";
import CrmPageHeader from "@/components/CrmPageHeader.vue";
import CellActions from "./components/CellActions.vue";
import CredentialFormModal from "./components/CredentialFormModal.vue";
import AssignModal from "./components/AssignModal.vue";
import { useCredencialesPortales } from "./useCredencialesPortales.js";

export default {
    name: "CredencialesPortales",
    components: {
        SoftButton, SoftModal, SoftSpinner, SoftAlert,
        CrmPageHeader, CellActions, CredentialFormModal, AssignModal,
    },
    setup() {
        const {
            filterEmail, filterCode,
            spinner, isLoading, alertTable,
            showCredentialForm, showAssignModal, showCellActions,
            selectedPortal, selectedUser, selectedCell, existingCredentialData,
            usersData, portales, adminUser, subUsers,
            membershipUsers, membershipPagination, loadingMembers, fetchMembershipUsers,
            portalCredentialsList, loadingPortalCredentials, fetchPortalCredentialsList,
            getPortalFields, getUserPortalInfo, getCredentialType, adminHasCredentialForPortal,
            fetchUsers, onCellClick, openCredentialForm, openAssignModal,
            saveCredential, deleteCredential, revokeShared,
            batchAssign, batchUnassign, deleteShare, toggleStatus,
            closeCredentialForm, closeAssignModal, closeCellActions,
        } = useCredencialesPortales();

        fetchUsers();

        const singleAssign = () => {
            if (!selectedCell.value) return;
            const portal = selectedCell.value.portal;
            closeCellActions();
            openAssignModal(portal);
        };

        const getInitials = (user) => {
            if (!user?.name) return '?';
            return [user.name, user.last_name].filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
        };

        const onImgError = (event) => { event.target.style.display = 'none'; };

        return {
            filterEmail, filterCode,
            spinner, isLoading, alertTable,
            showCredentialForm, showAssignModal, showCellActions,
            selectedPortal, selectedUser, selectedCell, existingCredentialData,
            usersData, portales, adminUser, subUsers,
            membershipUsers, membershipPagination, loadingMembers, fetchMembershipUsers,
            portalCredentialsList, loadingPortalCredentials, fetchPortalCredentialsList,
            getPortalFields, getUserPortalInfo, getCredentialType, adminHasCredentialForPortal,
            fetchUsers, onCellClick, openCredentialForm, openAssignModal,
            saveCredential, deleteCredential, revokeShared,
            batchAssign, batchUnassign, deleteShare, toggleStatus,
            closeCredentialForm, closeAssignModal, closeCellActions,
            singleAssign, getInitials, onImgError,
        };
    },
};
</script>

<style scoped>
/* Filters bar */
.matrix-filters {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}

.filter-group { display: flex; flex-direction: column; gap: 5px; }

.filter-label {
    font-size: .68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .07em;
    color: #94A3B8;
}

.filter-input-wrap { position: relative; }

.filter-input-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: .75rem;
    color: #94A3B8;
    pointer-events: none;
}

.filter-input {
    padding: 7px 12px 7px 30px;
    border: 1.5px solid #E2E8F0;
    border-radius: 8px;
    font-size: .78rem;
    color: #1E293B;
    background: #F8FAFC;
    outline: none;
    width: 220px;
    transition: border-color .15s ease, box-shadow .15s ease;
}
.filter-input:focus {
    border-color: #2563EB;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
}

.filter-actions { display: flex; gap: 8px; align-items: flex-end; }

/* Buttons */
.cred-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: .78rem;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: all .15s ease;
}
.cred-btn--primary {
    background: linear-gradient(135deg,#1E3A5F,#2563EB);
    color: #fff;
    box-shadow: 0 3px 10px rgba(37,99,235,0.22);
}
.cred-btn--primary:hover:not(:disabled) {
    box-shadow: 0 5px 16px rgba(37,99,235,0.32);
    transform: translateY(-1px);
}
.cred-btn--primary:disabled { opacity: .6; cursor: not-allowed; }
.cred-btn--outline {
    background: transparent;
    color: #475569;
    border: 1.5px solid #E2E8F0;
}
.cred-btn--outline:hover { border-color: #2563EB; color: #2563EB; background: #EFF6FF; }

/* Matrix table */
.matrix-table {
    width: 100%;
    border-collapse: collapse;
}

.matrix-th {
    padding: 10px 14px;
    font-size: .68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .07em;
    color: #64748B;
    background: #F8FAFC;
    border-bottom: 1px solid #E2E8F0;
    white-space: nowrap;
}

.matrix-th--sticky {
    position: sticky;
    left: 0;
    z-index: 2;
    min-width: 220px;
    border-right: 2px solid #E2E8F0;
}

.matrix-th--portal { text-align: center; min-width: 110px; }

.portal-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.portal-logo {
    width: 24px;
    height: 24px;
    object-fit: contain;
    border-radius: 4px;
}

.matrix-td {
    padding: 10px 14px;
    border-bottom: 1px solid #F1F5F9;
    vertical-align: middle;
}

.matrix-td--sticky {
    position: sticky;
    left: 0;
    z-index: 1;
    background: #fff;
    border-right: 2px solid #E2E8F0;
}

.matrix-td--admin {
    border-left: 3px solid #2563EB;
    background: rgba(37,99,235,0.03) !important;
}

.matrix-row--admin .matrix-td--sticky {
    background: rgba(37,99,235,0.03) !important;
}

.matrix-td--cell {
    text-align: center;
    cursor: pointer;
    transition: background .15s ease;
}
.matrix-td--cell:hover { background: #EFF6FF; }

/* User cell */
.user-cell { display: flex; align-items: center; gap: 10px; }

.user-avatar {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: .72rem;
    font-weight: 700;
    color: #fff;
    flex-shrink: 0;
}
.user-avatar--admin { background: linear-gradient(135deg,#1E3A5F,#2563EB); }
.user-avatar--sub   { background: linear-gradient(135deg,#475569,#64748B); }

.user-name { font-size: .82rem; font-weight: 600; color: #1E293B; margin: 0; }
.user-email { font-size: .7rem; color: #94A3B8; margin: 0; display: flex; align-items: center; gap: 6px; }

.admin-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: 10px;
    font-size: .6rem;
    font-weight: 700;
    background: #EFF6FF;
    color: #2563EB;
    text-transform: uppercase;
    letter-spacing: .05em;
}

/* Credential chips */
.cred-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: .65rem;
    font-weight: 600;
    margin-bottom: 3px;
}
.cred-chip--own    { background: #ECFDF5; color: #059669; }
.cred-chip--shared { background: #E0F2FE; color: #0284C7; }

.cred-status {
    display: block;
    font-size: .63rem;
    font-weight: 600;
}
.cred-status i { font-size: 5px; margin-right: 3px; }
.cred-status--active   { color: #059669; }
.cred-status--inactive { color: #DC2626; }

.cred-empty { color: #CBD5E1; font-size: .75rem; }

/* Loading overlay */
.loading-overlay {
    position: fixed;
    inset: 0;
    background: rgba(255,255,255,0.7);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    backdrop-filter: blur(2px);
}
</style>
